import React, { useEffect } from "react";
import { gql, useLazyQuery } from "@apollo/client";
import { withStyles } from "@material-ui/core/styles";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Typography,
  FormControl
} from "@material-ui/core";
import { useParams } from "react-router-dom";
import HistoryGameList from "./HistoryGameList";
import AutocompleteInput from "../AutocompleteInput";
import TableLink from "../TableLink";
import ShareButton from "../ShareButton";
import { DEFAULT_TITLE } from "../../constants";

const styles = theme => ({
  root: {
    maxWidth: 1200,
    marginTop: theme.spacing(5),
    padding: theme.spacing(2),
    overflowX: "auto",
    margin: "auto"
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 300,
    maxWidth: 500
  },
  tableHeader: {
    marginTop: theme.spacing(2)
  }
});

function assembleQuery(type, opponentType) {
  var fieldSet = `
  id,
  wins,
  losses,
  powers,
  gets,
  negs,
  points,
  tossups_heard,
  pp20tuh`;

  var query = `
  query historyQuery(
    $id: Int,
    $opponentId: Int
  ) {`;

  query += `
  MainVersus:${type}Versus${opponentType}(id:$id, opponentId:$opponentId) {
    ${type === "School" ? "short_" : ""}name,
    ${fieldSet}
  }`;

  query += `
  OpponentVersus:${opponentType}Versus${type}(id:$opponentId, opponentId:$id) {
    ${opponentType === "School" ? "short_" : ""}name,
    ${fieldSet}
  }`;

  query += `
  Main:${type}(id: $id) {
    ${type === "School" ? "short_" : ""}name,
    ${fieldSet}
  }
  `;
  query += `
  Opponent:${opponentType}(id: $opponentId) {
    ${opponentType === "School" ? "short_" : ""}name,
    ${fieldSet} 
  }}
  `;
  return query;
}

function History(props) {
  const { classes, type, opponentType, history } = props;
  const { id, opponentId } = useParams();
  const nameField = type === "School" ? "short_name" : "name";
  const opponentNameField = opponentType === "School" ? "short_name" : "name";

  const [execute, { loading, data, error }] = useLazyQuery(
    gql(assembleQuery(type, opponentType)),
    {
      variables: {
        id: parseInt(id),
        opponentId: parseInt(opponentId)
      }
    }
  );

  React.useEffect(() => {
    if (type && opponentType && id && opponentId) {
      execute();
    }
  }, [execute, type, opponentType, id, opponentId]);

  const handleChangeMain = newValue => {
    history.push(
      `/${newValue.type}/${newValue.id}/history${
        opponentId ? `/${opponentType}/${opponentId}` : ""
      }`
    );
  };

  const handleChangeOpponent = newValue => {
    history.push(`/${type}/${id}/history/${newValue.type}/${newValue.id}`);
  };

  useEffect(() => {
    document.title = `History - ${DEFAULT_TITLE}`;
  });

  return (
    <Paper className={classes.root}>
      <Typography variant="h4" gutterBottom>
        View History Between Players and Schools
        <ShareButton pageType="history"></ShareButton>
      </Typography>
      <div>
        <FormControl className={classes.formControl}>
          <AutocompleteInput
            type={type}
            id={parseInt(id)}
            onChange={handleChangeMain}
          ></AutocompleteInput>
        </FormControl>
        <FormControl className={classes.formControl}>
          <AutocompleteInput
            type={opponentType}
            id={parseInt(opponentId)}
            onChange={handleChangeOpponent}
          ></AutocompleteInput>
        </FormControl>
      </div>
      {loading && !error && <p>Loading...</p>}
      {error && !loading && <p>Error</p>}
      {data && !loading && !error && (
        <div>
          <Typography variant="h6" className={classes.tableHeader}>
            <TableLink to={`/${type}/${data.Main[0].id}`}>
              {data.Main[0][nameField]}
            </TableLink>
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell></TableCell>
                <TableCell>Points</TableCell>
                <TableCell>Wins</TableCell>
                <TableCell>Losses</TableCell>
                <TableCell>Powers</TableCell>
                <TableCell>Gets</TableCell>
                <TableCell>Negs</TableCell>
                <TableCell>Tossups Heard</TableCell>
                <TableCell>PP20TUH</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>Vs. {data.Opponent[0][opponentNameField]}</TableCell>
                <TableCell>
                  {data.MainVersus?.points?.toLocaleString()}
                </TableCell>
                <TableCell>{data.MainVersus?.wins?.toLocaleString()}</TableCell>
                <TableCell>
                  {data.MainVersus?.losses?.toLocaleString()}
                </TableCell>
                <TableCell>
                  {data.MainVersus?.powers?.toLocaleString()}
                </TableCell>
                <TableCell>{data.MainVersus?.gets?.toLocaleString()}</TableCell>
                <TableCell>{data.MainVersus?.negs?.toLocaleString()}</TableCell>
                <TableCell>
                  {data.MainVersus?.tossups_heard?.toLocaleString()}
                </TableCell>
                <TableCell>
                  {data.MainVersus?.pp20tuh?.toLocaleString()}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Overall</TableCell>
                <TableCell>{data.Main[0].points?.toLocaleString()}</TableCell>
                <TableCell>{data.Main[0].wins?.toLocaleString()}</TableCell>
                <TableCell>{data.Main[0].losses?.toLocaleString()}</TableCell>
                <TableCell>{data.Main[0].powers?.toLocaleString()}</TableCell>
                <TableCell>{data.Main[0].gets?.toLocaleString()}</TableCell>
                <TableCell>{data.Main[0].negs?.toLocaleString()}</TableCell>
                <TableCell>
                  {data.Main[0].tossups_heard?.toLocaleString()}
                </TableCell>
                <TableCell>{data.Main[0].pp20tuh?.toLocaleString()}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
          <Typography variant="h6" className={classes.tableHeader}>
            <TableLink to={`/${opponentType}/${data.Opponent[0].id}`}>
              {data.Opponent[0][opponentNameField]}
            </TableLink>
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell></TableCell>
                <TableCell>Points</TableCell>
                <TableCell>Wins</TableCell>
                <TableCell>Losses</TableCell>
                <TableCell>Powers</TableCell>
                <TableCell>Gets</TableCell>
                <TableCell>Negs</TableCell>
                <TableCell>Tossups Heard</TableCell>
                <TableCell>PP20TUH</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>Vs. {data.Main[0][nameField]}</TableCell>
                <TableCell>
                  {data.OpponentVersus?.points?.toLocaleString()}
                </TableCell>
                <TableCell>
                  {data.OpponentVersus?.wins?.toLocaleString()}
                </TableCell>
                <TableCell>
                  {data.OpponentVersus?.losses?.toLocaleString()}
                </TableCell>
                <TableCell>
                  {data.OpponentVersus?.powers?.toLocaleString()}
                </TableCell>
                <TableCell>
                  {data.OpponentVersus?.gets?.toLocaleString()}
                </TableCell>
                <TableCell>
                  {data.OpponentVersus?.negs?.toLocaleString()}
                </TableCell>
                <TableCell>
                  {data.OpponentVersus?.tossups_heard?.toLocaleString()}
                </TableCell>
                <TableCell>
                  {data.OpponentVersus?.pp20tuh?.toLocaleString()}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Overall</TableCell>
                <TableCell>
                  {data.Opponent[0].points?.toLocaleString()}
                </TableCell>
                <TableCell>{data.Opponent[0].wins?.toLocaleString()}</TableCell>
                <TableCell>
                  {data.Opponent[0].losses?.toLocaleString()}
                </TableCell>
                <TableCell>
                  {data.Opponent[0].powers?.toLocaleString()}
                </TableCell>
                <TableCell>{data.Opponent[0].gets?.toLocaleString()}</TableCell>
                <TableCell>{data.Opponent[0].negs?.toLocaleString()}</TableCell>
                <TableCell>
                  {data.Opponent[0].tossups_heard?.toLocaleString()}
                </TableCell>
                <TableCell>
                  {data.Opponent[0].pp20tuh?.toLocaleString()}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>
      )}
      {data && !loading && !error && data.MainVersus && data.OpponentVersus && (
        <HistoryGameList
          id={parseInt(id)}
          opponentId={parseInt(opponentId)}
          type={type}
          opponentType={opponentType}
          totalGames={data.MainVersus.wins + data.MainVersus.losses}
          mainName={data.MainVersus[nameField]}
          opponentName={data.OpponentVersus[opponentNameField]}
        ></HistoryGameList>
      )}
    </Paper>
  );
}

export default withStyles(styles)(History);
